/**
 * @fileoverview Base movement logic for all piece types in the Zodiac game engine.
 * Provides core functionality for determining valid moves and inspecting board state.
 *
 * @llm IMPORTANT: Game engine files maintain strict separation from UI code.
 * @llm PATTERN: Movement classes inherit from BaseMovement to reuse core logic.
 * @llm AVOID: UI-specific logic or direct DOM manipulation.
 *
 * ARCHITECTURAL GUIDELINES:
 * - Pure Functions: Methods should be pure when possible to facilitate testing
 * - Type Safety: All methods have explicit parameter and return types
 * - Inheritance Pattern: Base class for specialized piece movement classes
 *
 * RESPONSIBILITIES:
 * - Provides utility methods for checking board positions
 * - Determines square occupancy and piece relationships
 * - Creates Move objects based on piece interactions
 * - Supports special movement mechanics (housing, capturing, etc.)
 */
import { allClimbingPieces } from "../const";
import { Move } from "../Move";
import {
  Position,
  ISquare,
  PieceType,
  IPiece,
  MoveType,
  IMove,
  Effect,
} from "../types";
export interface SquareInfo {
  // todo these might not be null
  basicSquareInfo: BasicSquareInfo | null;
  housedTreeSquareInfo: HousedTreeSquareInfo | null;
  stoodGoatSquareInfo: StoodGoatSquareInfo | null;
  infectedVirusSquareInfo: InfectedVirusSquareInfo | null;
}

export interface HousedTreeSquareInfo {
  pieces: IPiece[];
}

export interface StoodGoatSquareInfo {
  piece: IPiece;
  faintedGoat: IPiece;
}

export interface InfectedVirusSquareInfo {}

export interface BasicSquareInfo {
  piece: IPiece | null;
  isEmpty: boolean;
  isEnemyOccupied: boolean;
  isAlly: boolean;
  isTree: boolean;
  isZombified: boolean;
}

export interface CompositeSquareInfo {
  isEmpty: boolean;
  piecesInfo: BasicSquareInfo[];
}

export class BaseMovement {
  onBoard(board: ISquare[][], position: Position): boolean {
    return !(
      position.col < 0 ||
      position.col + 1 > board[0].length ||
      position.row < 0 ||
      position.row + 1 > board.length
    );
  }

  getSquareInfo(square: ISquare, isLight: boolean): SquareInfo {
    // there are three scenarios where there are multiple pieces on a single square, Virus can combine with either of the others

    // a piece housed in a tree
    if (square.Pieces.length > 1 && square.Pieces.some((p) => p.isHousing)) {
      return {
        basicSquareInfo: null,
        housedTreeSquareInfo: {
          pieces: square.Pieces,
        },
        stoodGoatSquareInfo: null,
        infectedVirusSquareInfo: null,
      };
    }

    // If there are multiple pieces on a square, including a virus
    if (
      square.Pieces.length > 1 &&
      square.Pieces.some((p) => p.type === PieceType.Virus)
    ) {
      // Check if there's a non-virus piece that's an ally - we should not allow movement to this square
      const hasAllyNonVirus = square.Pieces.some(
        (p) => p.type !== PieceType.Virus && p.isLight === isLight
      );
      if (hasAllyNonVirus) {
        // Return as if the square is occupied by an ally (can't move here)
        return {
          basicSquareInfo: {
            isEmpty: false,
            isEnemyOccupied: false,
            isAlly: true,
            isTree: false,
            isZombified: false,
            piece:
              square.Pieces.find((p) => p.type !== PieceType.Virus) ?? null,
          },
          housedTreeSquareInfo: {
            pieces: square.Pieces,
          },
          stoodGoatSquareInfo: null,
          infectedVirusSquareInfo: null,
        };
      }
    }

    // a piece standing on a fainted goat
    if (
      square.Pieces.length === 2 &&
      square.Pieces.some((p) => p.type === PieceType.Goat && p.isFainted)
    ) {
      // Find the non-fainted goat piece
      const otherPiece = square.Pieces.find(
        (p) => !(p.type === PieceType.Goat && p.isFainted)
      );

      if (otherPiece) {
        // Return info as if only the other piece is on the square (ignore the fainted goat)
        // This ensures proper capturing behavior
        return {
          basicSquareInfo: {
            isEmpty: false,
            isEnemyOccupied: isLight !== otherPiece.isLight,
            isAlly: isLight === otherPiece.isLight,
            isTree: otherPiece.type === PieceType.Tree,
            isZombified: otherPiece.zombifiedStatus.isZombified,
            piece: otherPiece,
          },
          housedTreeSquareInfo: null,
          stoodGoatSquareInfo: {
            piece: otherPiece,
            faintedGoat: square.Pieces.find(
              (p) => p.type === PieceType.Goat && p.isFainted
            )!,
          },
          infectedVirusSquareInfo: null,
        };
      }
    }

    // a piece infected by the virus

    return {
      basicSquareInfo: this.getBasicInfo(square, isLight),
      housedTreeSquareInfo: {
        pieces: square.Pieces,
      },
      stoodGoatSquareInfo: null,
      infectedVirusSquareInfo: null,
    };
  }

  getBasicInfo(square: ISquare, isLight: boolean): BasicSquareInfo {
    let isEmpty: boolean = true;
    let isEnemyOccupied: boolean = false;
    let isAlly: boolean = false;
    let isTree: boolean = false;
    let isZombified: boolean = false;

    // If there are no pieces, it's just empty
    if (square.Pieces.length === 0) {
      return {
        isEmpty: true,
        isEnemyOccupied: false,
        isAlly: false,
        isTree: false,
        isZombified: false,
        piece: null,
      };
    }

    // If there's exactly one piece, handle it normally
    if (square.Pieces.length === 1) {
      var piece = square.Pieces[0];
      isEmpty = false;
      isEnemyOccupied = isLight !== piece.isLight;
      isAlly = isLight === piece.isLight;
      isTree = piece.type === PieceType.Tree;
      isZombified = piece.zombifiedStatus.isZombified;
    }
    // Special case: Check if there's a fainted goat and another piece
    else if (
      square.Pieces.length === 2 &&
      square.Pieces.some((p) => p.type === PieceType.Goat && p.isFainted)
    ) {
      // Find the non-fainted goat piece
      const otherPiece = square.Pieces.find(
        (p) => !(p.type === PieceType.Goat && p.isFainted)
      );

      // If found, use this piece for status determination (ignoring the fainted goat)
      if (otherPiece) {
        isEmpty = false;
        isEnemyOccupied = isLight !== otherPiece.isLight;
        isAlly = isLight === otherPiece.isLight;
        isTree = otherPiece.type === PieceType.Tree;
        isZombified = otherPiece.zombifiedStatus.isZombified;

        return {
          isEmpty,
          isEnemyOccupied,
          isAlly,
          isTree,
          isZombified,
          // Use the non-fainted goat piece as the representative piece
          piece: otherPiece,
        };
      }
    }
    // If there are multiple pieces (likely a virus with another piece)
    else {
      isEmpty = false;

      // Look for a non-virus piece to determine alliance
      const nonVirusPiece = square.Pieces.find(
        (p) => p.type !== PieceType.Virus
      );
      if (nonVirusPiece) {
        isEnemyOccupied = isLight !== nonVirusPiece.isLight;
        isAlly = isLight === nonVirusPiece.isLight;
        isTree = nonVirusPiece.type === PieceType.Tree;
        isZombified = nonVirusPiece.zombifiedStatus.isZombified;
      }
      // If all pieces are viruses, just use the first one
      else {
        const piece = square.Pieces[0];
        isEnemyOccupied = isLight !== piece.isLight;
        isAlly = isLight === piece.isLight;
      }
    }

    // For a square with multiple pieces, find the most significant piece to return
    if (square.Pieces.length > 1) {
      // Try to find a non-fainted goat, non-virus piece first
      const significantPiece = square.Pieces.find(
        (p) =>
          !(p.type === PieceType.Goat && p.isFainted) &&
          p.type !== PieceType.Virus
      );

      if (significantPiece) {
        return {
          isEmpty,
          isEnemyOccupied,
          isAlly,
          isTree,
          isZombified,
          piece: significantPiece,
        };
      }
    }

    return {
      isEmpty,
      isEnemyOccupied,
      isAlly,
      isTree,
      isZombified,
      // Always use the first piece as the representative piece
      piece: square.Pieces[0] ?? null,
    };
  }

  getCompositeSquareInfo(
    square: ISquare,
    isLight: boolean
  ): CompositeSquareInfo {
    let isEmpty = square.Pieces.length === 0;
    let piecesInfo: BasicSquareInfo[] = [];

    for (const piece of square.Pieces) {
      const pieceInfo: BasicSquareInfo = {
        isEmpty: false,
        isEnemyOccupied: isLight !== piece.isLight,
        isAlly: isLight === piece.isLight,
        isTree: piece.type === PieceType.Tree,
        isZombified: piece.zombifiedStatus.isZombified,
        piece: piece,
      };
      piecesInfo.push(pieceInfo);
    }

    return {
      isEmpty,
      piecesInfo,
    };
  }

  getMove(
    fromPosition: Position,
    toPosition: Position,
    basicSquareInfo: BasicSquareInfo,
    piece: IPiece, // current piece
    capturablePieceTypes: PieceType[]
  ): Move | undefined {
    var isHouseable =
      basicSquareInfo.isAlly &&
      basicSquareInfo.isTree &&
      allClimbingPieces.includes(piece.type);
    var isCraneHouseable =
      piece.type === PieceType.Crane &&
      basicSquareInfo.isEnemyOccupied &&
      basicSquareInfo.isTree;

    var isRhinoTrample = piece.type === PieceType.Rhino;

    var isCapturable =
      basicSquareInfo.isEnemyOccupied &&
      basicSquareInfo.piece?.type !== PieceType.Rhino &&
      capturablePieceTypes.includes(basicSquareInfo.piece!.type);

    // Check if the target is a fainted goat and the attacking piece is not a Rhino or Tree
    // Fainted goats can only be captured by Rhino or Tree
    if (
      isCapturable &&
      basicSquareInfo.piece?.type === PieceType.Goat &&
      basicSquareInfo.piece?.isFainted &&
      piece.type !== PieceType.Rhino &&
      piece.type !== PieceType.Tree
    ) {
      isCapturable = false;
    }

    // Special rhino capture rules - separate logic for different piece types
    var isRhinoTarget =
      basicSquareInfo.isEnemyOccupied &&
      basicSquareInfo.piece?.type === PieceType.Rhino &&
      capturablePieceTypes.includes(PieceType.Rhino);

    // Bears and Wolves can capture rhino from any position
    var isBearOrWolf =
      piece.type === PieceType.Bear || piece.type === PieceType.Wolf;

    // Other pieces can only capture from side or back
    var isAttackingFromSideOrBack = piece.isLight
      ? toPosition.row <= fromPosition.row
      : toPosition.row >= fromPosition.row;

    var isRhinoCapturable =
      isRhinoTarget && (isBearOrWolf || isAttackingFromSideOrBack);

    // First check for capturing moves
    if (isCapturable || isRhinoCapturable) {
      // if the piece is a frog we want to add a poison side effect
      if (basicSquareInfo.piece?.type === PieceType.Frog) {
        var move = new Move(
          piece,
          toPosition,
          MoveType.capture,
          basicSquareInfo.piece
        );
        move.addSideEffect(toPosition, [piece], Effect.Poison());
        return move;
      }

      return new Move(
        piece,
        toPosition,
        MoveType.capture,
        basicSquareInfo.piece
      );
    }

    // Then check for tree housing
    if (isHouseable || isCraneHouseable) {
      return new Move(piece, toPosition, MoveType.house, basicSquareInfo.piece);
    }

    if (isRhinoTrample) {
      return new Move(
        piece,
        toPosition,
        MoveType.trample,
        basicSquareInfo.piece
      );
    }

    if (basicSquareInfo.isEmpty) {
      return new Move(
        piece,
        toPosition,
        MoveType.regular,
        basicSquareInfo.piece
      );
    }

    // For fainted goats - allow regular moves to their position instead of captures
    // This enables pieces to stand on fainted goats ONLY if there's not already another piece there
    if (
      basicSquareInfo.piece?.type === PieceType.Goat &&
      basicSquareInfo.piece?.isFainted
    ) {
      return new Move(piece, toPosition, MoveType.regular, null);
    }

    // this square cannot be moved to
    return undefined;
  }

  /**
   * Helper method to determine if a piece can move to a square that has multiple pieces
   * @param square The target square
   * @param isLight Whether the moving piece is light
   * @returns true if the piece can move to this square, false otherwise
   */
  canMoveToMultiPieceSquare(square: ISquare, isLight: boolean): boolean {
    // If the square has a tree that is housing another piece
    if (square.Pieces.some((p) => p.isHousing)) {
      // Movement to tree housing squares is handled by specific piece logic
      return false;
    }

    // Check for virus-infected piece
    if (square.Pieces.some((p) => p.type === PieceType.Virus)) {
      // Look for non-virus pieces
      const nonVirusPieces = square.Pieces.filter(
        (p) => p.type !== PieceType.Virus
      );

      // If there are non-virus pieces and any of them are allies, can't move there
      if (nonVirusPieces.some((p) => p.isLight === isLight)) {
        return false;
      }

      // Default to not allowing multi-piece squares unless specifically overridden
      return false;
    }

    // Default to not allowing multi-piece squares
    return false;
  }
}
